import { ReferenceDataOptionLists } from '../../../lib/constants/reference-data-option-lists';
import { Address } from '../../../lib/models/api/address.model';
import { Deserializable } from '../../../lib/models/deserializable';
import { ApplicantEmploymentAccountantType } from './applicant-employment-accountant-type.model';
import { ApplicantEmploymentCompanyDirectorship } from './applicant-employment-company-directorship.model';
import { ApplicantEmploymentPrivatePensionSource } from './applicant-employment-private-pension-source.model';

export class ApplicantEmployment implements Deserializable {
  public EmploymentNumber: number;
  public ApplicantNumber?: number;
  public AccountantsFaxNumber: string;
  public AccountantsPhoneNumber: string;
  public AccountantsName: string;
  public AnnualSalary?: number;
  public EmployeeNumber: string;
  public EmploymentStatusCode: string;
  public EmploymentTypeCode: string;
  public AccountantAddressID?: number;
  public EmployerAddressID?: number;
  public GuaranteedBonus?: number;
  public Has2YearsAccountsCode: string;
  public MonthsInEmployment?: number;
  public NameOfEmployer: string;
  public NatureOfBusiness: string;
  public NonGuaranteedBonus?: number;
  public OtherRegularIncome?: number;
  public Owns25PercentOfBusinessCode: string;
  public TaxReferenceNumber: string;
  public TotalAssessableIncome?: number;
  public TotalAnnualIncomePreviousYear?: number;
  public YearsInEmployment?: number;
  public IntendedRetirementAge?: number;
  public TotalAnnualPensionIncomeExpected?: number;
  public EventsWhichAffectIncomeCode: string;
  public DetailsOfChangeIncome: string;
  public ImpactOnIncome: string;
  public DateEffectOfAffectIncome: string;
  public OtherRegularIncomeSource: string;
  public HasAccountForPreviousFinancialYearCode: string;
  public EmploymentTypeDetails: string;
  public AllPreviousEmployersWithin12MonthsDetails: string;
  public IsAllIncomePaidInSterlingCode: string;
  public HRAddressID?: number;
  public HasAnAccountant: string;
  public HasDifferentHRAddress: string;
  public IsAbleToSupplyPayslips: string;
  public YearEnding1?: number;
  public YearEnding2?: number;
  public NetProfit1?: number;
  public NetProfit2?: number;
  public FosteringIncome?: number;
  public MaintenanceIncome?: number;
  public GrossAnnualContractIncome?: number;
  public ContractEndDate?: Date;
  public YearsExperienceInSectorAsContractor?: number;
  public MonthsExperienceInSectorAsContractor?: number;
  public CashEarningsPercentage?: number;
  public OccupationCode: string;
  public AreYouInReceiptOfPensionCode: string;
  public AnnualStatePension?: number;
  public AnnualPension?: number;
  public CompanyTypeCode: string;
  public EstablishedDate?: Date;
  public HasSecondSourceOfEmploymentCode: string;
  public PaidSalaryCurrencyCode: string;
  public DOEOccupationCode: string;
  public ProfessionalApplicantCode: string;
  public StateEmployeeCode: string;
  public ApplicantEmploymentOrder?: number;
  public IsSecondaryEmployment?: boolean;
  public PaidSalaryCurrency: string;
  public CarOrShiftAllowance?: number;
  public Overtime?: number;
  public OvertimeTypeCode: string;
  public Bonus?: number;
  public BonusTypeCode: string;
  public Commision?: number;
  public CommisionTypeCode: string;
  public P60LastFigure?: number;
  public P60FigurePreviousYear?: number;
  public P60FigureThreeYearsAgo?: number;
  public P60SalaryDifferenceExplanation: string;
  public OccupationTypeCode: string;
  public NumberOfCompanyDirectorshipHoldings?: number;
  public CurrentMarginalTaxRateCode: string;
  public AccountantAddress: Address;
  public EmployerAddress: Address;
  public HRAddress: Address;
  public ApplicantEmploymentAccountantTypes: Array<ApplicantEmploymentAccountantType>;
  public ApplicantEmploymentCompanyDirectorships: Array<ApplicantEmploymentCompanyDirectorship>;
  public ApplicantEmploymentPrivatePensionSources: Array<ApplicantEmploymentPrivatePensionSource>;

  public TotalAnnualPrivatePension = 0;
  public referenceDataOptionLists = ReferenceDataOptionLists;

  constructor() {
    this.AccountantAddress = new Address();
    this.EmployerAddress = new Address();
    this.HRAddress = new Address();
    this.EmployerAddress.CountryTypeCode = this.referenceDataOptionLists.CountryTypeUk;
    this.ApplicantEmploymentAccountantTypes = new Array<ApplicantEmploymentAccountantType>();
    this.ApplicantEmploymentCompanyDirectorships = new Array<ApplicantEmploymentCompanyDirectorship>();
    this.ApplicantEmploymentPrivatePensionSources = new Array<ApplicantEmploymentPrivatePensionSource>();
  }

  deserialize(input: any): this {
    Object.assign(this, input);
    this.AccountantAddress = new Address().deserialize(input.AccountantAddress);
    this.EmployerAddress = new Address().deserialize(input.EmployerAddress);
    this.HRAddress = new Address().deserialize(input.HRAddress);
    if (input.ApplicantEmploymentAccountantTypes) {
      this.ApplicantEmploymentAccountantTypes = input.ApplicantEmploymentAccountantTypes.map(
        (applicantEmploymentAccountantType) =>
          new ApplicantEmploymentAccountantType().deserialize(applicantEmploymentAccountantType)
      );
    } else {
      this.ApplicantEmploymentAccountantTypes = new Array<ApplicantEmploymentAccountantType>();
    }
    if (this.ApplicantEmploymentCompanyDirectorships) {
      this.ApplicantEmploymentCompanyDirectorships = input.ApplicantEmploymentCompanyDirectorships.map(
        (applicantEmploymentCompanyDirectorship) =>
          new ApplicantEmploymentCompanyDirectorship().deserialize(applicantEmploymentCompanyDirectorship)
      );
    }
    if (input.ApplicantEmploymentPrivatePensionSources) {
      this.ApplicantEmploymentPrivatePensionSources = input.ApplicantEmploymentPrivatePensionSources.map(
        (applicantEmploymentPrivatePensionSource) =>
          new ApplicantEmploymentPrivatePensionSource().deserialize(applicantEmploymentPrivatePensionSource)
      );
    } else {
      this.ApplicantEmploymentPrivatePensionSources = new Array<ApplicantEmploymentPrivatePensionSource>();
    }

    return this;
  }

  isNotEmployed(): boolean {
    return this.EmploymentStatusCode === this.referenceDataOptionLists.EmploymentStatusNotEmployed;
  }

  isEmployed(): boolean {
    return this.EmploymentStatusCode === this.referenceDataOptionLists.EmploymentStatusEmployed;
  }

  isSelfEmployed(): boolean {
    return this.EmploymentStatusCode === this.referenceDataOptionLists.EmploymentStatusSelfEmployed;
  }

  isRetired(): boolean {
    return this.EmploymentStatusCode === this.referenceDataOptionLists.EmploymentStatusRetired;
  }

  isEmployedOrSelfEmployed(): boolean {
    return this.isEmployed() || this.isSelfEmployed();
  }

  isNotUnemployed(): boolean {
    return this.isRetired() || this.isEmployedOrSelfEmployed();
  }

  isContractor(): boolean {
    return this.EmploymentTypeCode === this.referenceDataOptionLists.EmploymentTypeContractor;
  }

  isEmployedButNotAsContractor(): boolean {
    return this.isEmployed() && !this.isContractor();
  }

  isEmployedAsContractor(): boolean {
    return this.isEmployed() && this.isContractor();
  }

  calculateTotalAssessableIncome(): number {
    let averageNetProfit = 0;
    if (this.NetProfit1) {
      averageNetProfit = Math.floor(((this.NetProfit1 || 0) + (this.NetProfit2 || 0)) / 2);
      if (this.NetProfit1 <= averageNetProfit) {
        averageNetProfit = this.NetProfit1;
      }
    }

    if (averageNetProfit > 0) {
      this.TotalAssessableIncome = averageNetProfit;
    } else if (this.NetProfit1 || this.NetProfit1 === 0 || this.NetProfit2 || this.NetProfit2 === 0) {
      this.TotalAssessableIncome = 0;
    } else {
      this.TotalAssessableIncome = undefined;
    }

    return averageNetProfit;
  }

  calculateRegularIncome(): number {
    let regularIncome = 0;
    regularIncome += this.AnnualSalary || 0;
    regularIncome += this.NonGuaranteedBonus || 0;
    regularIncome += this.OtherRegularIncome || 0;
    regularIncome += this.FosteringIncome || 0;
    regularIncome += this.MaintenanceIncome || 0;
    regularIncome += this.AnnualStatePension || 0;
    regularIncome += this.TotalAnnualPrivatePension || 0;
    regularIncome += this.GrossAnnualContractIncome || 0;

    return regularIncome;
  }
}
